import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useUpdateProfile } from './useUpdateProfile'

import { useApp } from '../../contexts/AppContext'
import { useProfileContext } from '../../contexts/ProfileContext'
import { Language } from '../../types/language'
import { BannerKind, ThemeType } from '../../types/contexts/AppContext'
import { PreferencesModel } from '../../types/user/userProfile'

export const useProfilePreferenceActions = () => {
  const { t } = useTranslation('account')
  const { theme, setTheme, lang, setLang, showBanner } = useApp()
  const { updateLocalProfile } = useProfileContext()
  const { updateProfile, loading, error, success, resetState } =
    useUpdateProfile()

  useEffect(() => {
    if (error) {
      showBanner({ kind: BannerKind.ERR, msg: error })
    }

    if (success) {
      showBanner({ kind: BannerKind.OK, msg: success })
    }

    resetState()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, success])

  const mergePreferences = (preferences: PreferencesModel) => {
    updateLocalProfile({
      language: preferences.language ?? Language.RO,
      theme: preferences.theme
        ? (preferences.theme.toLowerCase() as ThemeType)
        : ThemeType.LIGHT,
    })
  }

  const changeTheme = async (nextTheme: ThemeType) => {
    if (nextTheme === theme || loading) {
      return
    }

    const previousTheme = theme
    setTheme(nextTheme)

    const updatedPreferences = await updateProfile(
      [{ op: 'replace', path: '/theme', value: nextTheme.toUpperCase() }],
      t('account.hook.preferences.saved')
    )

    if (!updatedPreferences) {
      setTheme(previousTheme)
      return
    }

    mergePreferences(updatedPreferences)
  }

  const changeLanguage = async (nextLanguage: Language) => {
    if (nextLanguage === lang || loading) {
      return
    }

    const previousLanguage = lang
    setLang(nextLanguage)

    const updatedPreferences = await updateProfile(
      [{ op: 'replace', path: '/language', value: nextLanguage }],
      t('account.hook.preferences.saved')
    )

    if (!updatedPreferences) {
      setLang(previousLanguage)
      return
    }

    mergePreferences(updatedPreferences)
  }

  return {
    theme,
    language: lang,
    loading,
    changeTheme,
    changeLanguage,
  }
}
